import { CustomAttributeFilterRequest } from '@/modules/common/types/dto/request/custom-attribute-request';

export interface GetApprovalFilterRequest {
  startDate: string; // ISO date-time
  endDate: string; // ISO date-time
  projectIds?: number[];
  userIds?: number[];
  departmentIds?: number[];
  statuses?: string[];
  search?: string;
  customAttributeFilters?: CustomAttributeFilterRequest[];
}

export interface ApprovalProjectsResult {
  items: ApprovalProjectItemResponse[];
  totalWorklogs: number;
  pagination?: ApprovalPaginationMeta;
}

export interface ApprovalProjectItemResponse {
  id: number;
  name: string;
  /** Project color hex without the leading '#', or null when unset. */
  colorHex: string | null;
  logoUrl: string | null;
  trackedMinutes: number;
  pendingTrackedMinutes: number;
  bookedMinutes: number;
  approvedForInvoiceMinutes: number;
  approvedForBillMinutes: number;
  /**
   * Invoice minutes an approve of the still-pending worklogs would write. NOT the same as
   * `pendingTrackedMinutes`: unapproving keeps a worklog's approved overrides and a later approve
   * re-applies them, so an entry unapproved at 5h and tracked at 1h is approved at 5h again.
   */
  pendingApprovedForInvoiceMinutes: number;
  /** Same as `pendingApprovedForInvoiceMinutes`, for bill generation. */
  pendingApprovedForBillMinutes: number;
  approvalPercentage: number;
  totalWorklogs: number;
  approvedWorklogs: number;
  pendingWorklogs: number;
  hasEmptyDescription: boolean;
  timeOffMinutes: number;
  approvedTimeOffMinutes: number;
  pendingTimeOffMinutes: number;
  /** Weighted-average external rate in `currencyCode`; masked to -1 without the billing permission. */
  externalRate: number | null;
  /** Approved hours × external rate in `currencyCode`; masked to -1 without the billing permission. */
  approvedIncome: number | null;
  currencyCode: string | null;
  hash: string;
}

export interface ApprovalTeamMemberItemResponse {
  id: number;
  name: string;
  profileImageUrl: string | null;
  trackedMinutes: number;
  /** Tracked minutes of entries still awaiting approval. */
  pendingTrackedMinutes: number;
  bookedMinutes: number;
  /** Approved minutes invoice generation would use. */
  approvedForInvoiceMinutes: number;
  /** Approved minutes bill generation would use — may legitimately differ from the invoice value. */
  approvedForBillMinutes: number;
  /** Invoice minutes an approve of the still-pending worklogs would write (overrides retained). */
  pendingApprovedForInvoiceMinutes: number;
  /** Same as `pendingApprovedForInvoiceMinutes`, for bill generation. */
  pendingApprovedForBillMinutes: number;
  /** Approved-for-invoice ÷ booked, as a percentage. */
  approvalPercentage: number;
  totalWorklogs: number;
  /** Number of worklog entries already approved (unapprovable count). */
  approvedWorklogs: number;
  /** Number of worklog entries still awaiting approval (approvable count). */
  pendingWorklogs: number;
  hasEmptyDescription: boolean;
  timeOffMinutes: number;
  approvedTimeOffMinutes: number;
  pendingTimeOffMinutes: number;
  /** Weighted-average external rate in `currencyCode`; masked to -1 without the billing permission. */
  externalRate: number | null;
  /** Approved hours × external rate in `currencyCode`; masked to -1 without the billing permission. */
  approvedIncome: number | null;
  currencyCode: string | null;
  hash: string;
}

/**
 * Pagination metadata returned by the nested approval endpoints (team-members, worklogs).
 * `cursor` is opaque per the backend contract; the codebase treats it as `number | null`
 * (see `getTimeTracks`). Optional throughout so callers can be defensive: when absent,
 * pagination is treated as "no further pages".
 */
export interface ApprovalPaginationMeta {
  total: number;
  cursor?: number | null;
}

export interface ApprovalTeamMembersResponse {
  items: ApprovalTeamMemberItemResponse[];
  pagination?: ApprovalPaginationMeta;
}

export type WorklogApprovalStatusDTO = 'APPROVED' | null;

/** Discriminator for rows in the approval worklogs list — single source of truth for the wire format. */
export enum ApprovalWorklogRowTypeDTO {
  WORKLOG = 'WORKLOG',
  TIME_OFF = 'TIME_OFF',
}

export type ApprovalWorklogRowTypeResponse = ApprovalWorklogRowTypeDTO;

/** Time-off approval status carried by TIME_OFF rows of the approval worklogs list. */
export type ApprovalTimeOffStatusDTO = 'APPROVED' | 'PENDING';

export interface ApprovalWorklogItemResponse {
  rowType: ApprovalWorklogRowTypeResponse;
  /** Worklog ID for WORKLOG rows, time-off request day ID for TIME_OFF rows. */
  id: number;
  date: string;
  /** Null for TIME_OFF rows. */
  trackedMinutes: number | null;
  description: string | null;
  approvalStatus: WorklogApprovalStatusDTO | ApprovalTimeOffStatusDTO;
  approvedForInvoiceMinutes: number | null;
  approvedForBillMinutes: number | null;
  approvalNotes: string | null;
  /** Null for TIME_OFF rows — they are not selectable/approvable. */
  hash: string | null;
  /** TIME_OFF rows only. */
  timeOffTypeName: string | null;
  /** TIME_OFF rows only. */
  durationMinutes: number | null;
  /** External rate as of the worklog date in `currencyCode`; null for TIME_OFF rows, masked to -1 without the billing permission. */
  externalRate: number | null;
  /** Approved hours × external rate in `currencyCode`; null unless the entry is approved. */
  approvedIncome: number | null;
  currencyCode: string | null;
}

export interface ApprovalWorklogsResponse {
  items: ApprovalWorklogItemResponse[];
  pagination?: ApprovalPaginationMeta;
}

// =============================================================================
// Approve / unapprove request payloads
// =============================================================================
// The approve/unapprove endpoints accept exactly ONE of the three selection
// levels per request: projects, teamMembers, or worklogs (ids + hashes only).
// =============================================================================

export interface ApproveByProjectRequest {
  id: number;
  hash: string;
  /** Worklog ids explicitly deselected under this project selection — never updated. */
  excludedWorklogIds?: number[];
  /** User ids explicitly deselected under this project selection — none of their worklogs are updated. */
  excludedUserIds?: number[];
}

export interface ApproveByTeamMemberRequest {
  projectId: number;
  id: number;
  hash: string;
  /** Worklog ids explicitly deselected under this member selection — never updated. */
  excludedWorklogIds?: number[];
}

export interface ApproveByWorklogRequest {
  id: number;
  hash: string;
  /** Omitted → the backend approves at the worklog's tracked minutes. */
  approvedForInvoiceMinutes?: number;
  /** Omitted → the backend approves at the worklog's tracked minutes. */
  approvedForBillMinutes?: number;
  approvalNotes?: string;
}

export interface ApprovalSelectionRequest {
  projects?: ApproveByProjectRequest[];
  teamMembers?: ApproveByTeamMemberRequest[];
  worklogs?: ApproveByWorklogRequest[];
}

export interface ApprovalSummaryItemResponse {
  totalHoursAwaitingApproval: number;
  totalApprovedForInvoiceHours: number;
  totalApprovedForBillHours: number;
  /** One count for both values — an entry is either pending or approved. */
  numberOfEntriesPending: number;
  /** 0–1 ratio. */
  rateApprovedForInvoiceToBooked: number;
  /** 0–1 ratio. */
  rateApprovedForBillToBooked: number;
  totalWorklogs: number;
  totalTrackedHours: number;
  totalBookedHours: number;
  /** Converted to the organization currency; masked to -1 without the billing permission. */
  approvedIncome: number;
  /** Converted to the organization currency; masked to -1 without the billing permission. */
  pendingIncome: number;
  currencyCode: string | null;
}
